import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import { LayoutGroup, motion } from "framer-motion";
import useIsMobile from "hooks/useIsMobile.ts";
import { ChangeEvent, memo, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import IaContainer from "../../../components/IaContainer/IaContainer.tsx";
import { useCidade } from "../../../contexts/FilialContext.tsx";
import { useProfile } from "../../../contexts/ProfileContext.tsx";
import RequestControllerRedux from "../../../contexts/RequestController.js";
import { useToast } from "../../../contexts/ToastContext.tsx";
import CoreButton from "../../../core/components/Button.tsx";
import Icon, { IconNames } from "../../../core/components/Icon.tsx";
import MotionContainer from "../../../core/components/MotionContainer.tsx";
import Separator from "../../../core/components/Separator.tsx";
import SelectInputField from "../../../core/data-entry/SelectInputField.tsx";
import Layout from "../../../core/layout/Layout.tsx";
import pdf from "../../../images/Politica_de_Privacidade_-_Gerenciado_de_Transportes.pdf";
import "./Home.css";

type CardHomeProps = {
	iconName: IconNames;
	title: string;
	description: string;
	onClick?: () => void;
	children?: React.ReactNode;
};

type CookiesModalProps = {
	open: boolean;
	onAccept: () => void;
};

function CookiesModal({ open, onAccept }: CookiesModalProps) {
	return (
		<Dialog className="corpoModalCookies" open={open}>
			<DialogTitle>Esse site usa cookies</DialogTitle>
			<p className="texto-cookies">
				Nós armazenamos dados temporariamente para melhorar a sua experiência de
				navegação e recomendar conteúdo de seu interesse. Ao utilizar nossos
				serviços, você concorda com tal monitoramento.
			</p>
			<div className="botaoConcluir">
				<a className="texto-link" href={pdf} target="_blank" rel="noreferrer">
					Política de Privacidade
				</a>
				<Button sx={{ width: 180 }} variant="contained" onClick={onAccept}>
					Aceitar
				</Button>
			</div>
		</Dialog>
	);
}

function CardHome({ iconName, title, description, onClick, children }: CardHomeProps) {
	return (
		<div
			className={`card-home ${children ? "no-hover" : ""}`} onClick={onClick}>
			<div className="info-home-text">
				<Icon name={iconName} size="2xl" theme="dark" />
				<Separator direction="horizontal" size="md" />
				<span className="text-card-home">{title}</span>
			</div>
			<div className="info-home">
				<span>{description}</span>
			</div>
			{children}
		</div>
	)
};

function Home() {
	const navigate = useNavigate();
	const { dadosFilialRedux } = useCidade();
	const { dadosRequestControllerRedux, setDadosRequestControllerRedux } =
		useContext(RequestControllerRedux);
	const { handleSetContextoEmbarque, profile } = useProfile();

	const { showToast } = useToast();

	const filiaisFiltrada = useMemo(
		() =>
			dadosFilialRedux.filter((filial) => filial.nomeFilial !== "Modelo Viagens"),
		[dadosFilialRedux]
	);

	const defaultMemoizedFiliais = useMemo(() => {
		return filiaisFiltrada.map((item) => ({
			title: item.nomeFilial,
			obj: item.id,
		}))
	}, [filiaisFiltrada]);

	const [openCookies, setOpenCookies] = useState(false);
	const [selectedFilial, setSelectedFilial] = useState<{ title: string, obj: number } | null>();
	const [filiais, setFiliais] = useState<{ title: string, obj: number }[]>(defaultMemoizedFiliais);

	const handleCloseCookies = useCallback(() => {
		setOpenCookies(false);
		localStorage.setItem("Cookies", "Aceito");
	}, []);
	useEffect(() => {
		if (!localStorage.getItem("Cookies")) {
			setTimeout(() => {
				setOpenCookies(false);
			}, 5000);
		}
	}, []);

	useEffect(() => {
		if (dadosRequestControllerRedux === "Email Senha Codigo") {
			setDadosRequestControllerRedux("");
			showToast("Senha alterada com sucesso!", "success");
		}
	}, [dadosRequestControllerRedux, setDadosRequestControllerRedux, showToast]);

	const handleGoFilial = useCallback(
		(id: number) => {
			const filial = filiaisFiltrada.find((f) => f.id === id);
			if (!filial) return;
			handleSetContextoEmbarque(filial.contexto);
			navigate("/viagens/" + filial.contexto);
		},
		[navigate, handleSetContextoEmbarque, filiaisFiltrada]
	);

	const handleChangeSelecionarFilial = useCallback((option: { title: string, obj: number } | null) => {
		setSelectedFilial(option);
	}, [])

	const handleInputSelecionarFilialChange = useCallback(
		(inputValue: ChangeEvent<HTMLInputElement>) => {
			const normalizedInput = inputValue.target.value.trim().toLowerCase();

			if (normalizedInput === "") {
				setFiliais([]);
				return;
			}

			const filteredOptions = defaultMemoizedFiliais.filter((filial) =>
				filial.title.toLowerCase().includes(normalizedInput)
			);
			setFiliais(filteredOptions);
		}, [defaultMemoizedFiliais]);

	const handleClearSelecionarFilial = useCallback(() => {
		setSelectedFilial(null);
	}, []);

	return (
		<Layout showNavbar title="Home">
			<MotionContainer scroll animationKey="HomeScreenContainer">
				<LayoutGroup>
					<div
						className="content-container-scrollable"
						style={{ display: "flex", paddingTop: "5%", }}
					>
						<div className="container-bem-vindo">
							<p className="texto-bem-vindo">
								Bem-vindo,
								<Separator size="xs" direction="horizontal" />
								<span className="texto-usuario">{profile?.usuario.nome}</span>
							</p>
						</div>
						<Separator size="3xl" />
						<motion.div
							layout="position"
							layoutId="ia-container-wrapper"
							transition={{ layout: { duration: 0.24, ease: "easeOut" } }}
							className="container-ia"
						>
							<IaContainer />
						</motion.div>
						<Separator size="xl" />
						<motion.div
							layout="position" className="container-home">
							<CardHome
								iconName="Truck"
								title="Viagens"
								description="Consulte a listagem de todas as viagens."
							>
								<div className="select-wrapper">
									<SelectInputField
										label="Selecionar Viagem"
										placeholder="Selecionar Viagem"
										value={selectedFilial?.title || ""}
										options={filiais}
										onChange={handleChangeSelecionarFilial}
										onClear={handleClearSelecionarFilial}
										onInputChange={handleInputSelecionarFilialChange}
										maxDropdownHeight={250}
									/>
									<CoreButton
										onClick={() => {
											handleGoFilial(selectedFilial?.obj!);
										}}
										theme="green"
										label="Consultar"
										radius="2xl"
										disabled={!selectedFilial}
										fullWidthContent={useIsMobile(500)}
									/>
								</div>
							</CardHome>
							<CardHome
								iconName="ChartNoAxesCombined"
								title="Dashboards"
								description="Consulte os indicadores de embarques e faturamentos em detalhes."
								onClick={() => {
									navigate("/dashboard");
								}}
							/>
							{!useIsMobile() && (
								<CardHome
									iconName="FileChartColumn"
									title="Faturamento"
									description="Filtre e confira os relatórios de faturamento em detalhes."
									onClick={() => {
										navigate("/faturamento");
									}}
								/>)}
						</motion.div>
						{!useIsMobile(1000) && (
							<>
								<Separator size="xl" />
								<motion.div
									layout="position" className="container-home">
									<CardHome
										iconName="Container"
										title="Disponibilidade da Frota"
										description="Verifique a disponibilidade da frota em tempo real."
										onClick={() => {
											navigate("/disponibilidade-frota");
										}}
									/>
									<CardHome
										iconName="TriangleAlert"
										title="Divergências SAT"
										description="Gerencie e resolva divergências do SAT de forma eficiente."
										onClick={() => {
											navigate("/divergencias-sat");
										}}
									/>
								</motion.div>
							</>
						)}
						<Separator size="2xl" />
					</div>
				</LayoutGroup>
			</MotionContainer>
			<CookiesModal
				open={openCookies}
				onAccept={handleCloseCookies}
			/>
		</Layout>
	);
}

export default memo(Home);
